import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    ShieldCheck, Award, Gamepad2, BookOpen, Users, Sparkles, TrendingUp,
    HardHat, Zap, Building2, ChevronRight, ChevronLeft, Trophy, Brain, ArrowRight,
    PlayCircle, Languages, Smartphone, Search, Gift, Medal, Film,
    CircleDot, Star, BellRing, Repeat, Crown,
} from 'lucide-react';
import { api } from '@/lib/api';
import { useTheme } from '@/lib/theme';
import { ILLUSTRATIONS, ORDER, KBCIllustration } from './trainee/games/shared/GameIllustrations';

const BANNERS = [
    {
        eyebrow: 'New in the Arcade',
        title: 'Play Kaun Banega Cables-pati',
        sub: 'A full KBC-style hot-seat — voice questions, lifelines, segmented timers and up to 1 crore points.',
        cta: 'Play now',
        bg: 'from-amber-900 via-slate-900 to-black',
        accent: 'text-amber-300',
        Art: KBCIllustration,
    },
    {
        eyebrow: 'Industry-recognised',
        title: 'Get certified in Cables & Wires',
        sub: 'Earn QR-verifiable certificates recognised across the entire Propulse network — valid for a full year.',
        cta: 'Start a course',
        bg: 'from-brand-blue via-brand-blueDark to-slate-950',
        accent: 'text-brand-yellow',
        Art: CertArt,
    },
    {
        eyebrow: 'Built for Bharat',
        title: 'Learn in 10 Indian languages',
        sub: 'English, Hindi & 8 regional languages. Install it as an app on any phone — no Play Store needed.',
        cta: 'Get started',
        bg: 'from-emerald-700 via-teal-800 to-slate-950',
        accent: 'text-emerald-200',
        Art: LanguageArt,
    },
];

const CATEGORIES = [
    { title: 'Product Training', desc: 'FR, FRLS, XLPE cables & house-wire mastery', icon: Building2, color: 'from-brand-black to-[#3B3739]' },
    { title: 'Safety & Compliance', desc: 'PPE, LOTO, arc-flash protection', icon: ShieldCheck, color: 'from-brand-red to-rose-600' },
    { title: 'Channel Partner Excellence', desc: 'Drum storage, billing, counterfeit detection', icon: HardHat, color: 'from-brand-orange to-amber-600' },
    { title: 'Electrician Training', desc: 'IS 694 / IS 7098 wiring & troubleshooting', icon: Zap, color: 'from-yellow-500 to-amber-500' },
    { title: 'Sales Training', desc: 'Pitch, objections, closing skills', icon: TrendingUp, color: 'from-emerald-600 to-teal-600' },
    { title: 'Compliance', desc: 'Mandatory regulatory training', icon: Award, color: 'from-violet-600 to-fuchsia-600' },
];

const FEATURES = [
    { icon: Film, title: 'Video Reels', desc: 'A vertical, swipe-up reels feed of bite-sized product & safety clips — learn in 30 seconds.', color: 'from-rose-500 to-pink-600' },
    { icon: CircleDot, title: 'Daily Stories', desc: 'Instagram-style stories with the latest launches, tips and CTAs — fresh every day.', color: 'from-fuchsia-500 to-purple-600' },
    { icon: Languages, title: '10 Indian Languages', desc: 'Full UI in English, Hindi & 8 regional languages — learn in the language you think in.', color: 'from-emerald-500 to-teal-600' },
    { icon: Smartphone, title: 'Install as an App', desc: 'Add to home screen as a PWA. Works offline-first on any phone, no Play Store needed.', color: 'from-sky-500 to-blue-600' },
    { icon: Search, title: 'Smart Search & Voice', desc: 'Type-ahead search with voice input, plus AI recommendations tailored to your role.', color: 'from-amber-500 to-orange-600' },
    { icon: BookOpen, title: 'AI-Generated Courses', desc: 'Rich, structured courses with quizzes — auto-built and kept current by AI.', color: 'from-indigo-500 to-violet-600' },
    { icon: Medal, title: 'Pathways & Resume', desc: 'Guided learning pathways and a sharable digital resume of every skill you earn.', color: 'from-cyan-500 to-teal-600' },
    { icon: Gift, title: 'Rewards & Loyalty', desc: 'Earn points on everything and redeem across the Aditya Birla loyalty network.', color: 'from-brand-yellow to-amber-500', dark: true },
];

export default function PublicHome() {
    const [stats, setStats] = useState(null);
    const [games, setGames] = useState([]);

    useEffect(() => {
        api.get('/courses').then((r) => setStats({ courses: r.data.length })).catch(() => {});
        api.get('/games').then((r) => setGames(r.data)).catch(() => {});
    }, []);

    const sortedGames = [...games].sort((a, b) => {
        const ai = ORDER.indexOf(a.code); const bi = ORDER.indexOf(b.code);
        return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
    });

    return (
        <div data-testid="public-home">
            {/* BANNER CAROUSEL */}
            <BannerCarousel />

            {/* HERO */}
            <section className="relative overflow-hidden bg-hero-pattern">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 lg:pt-24 pb-20 lg:pb-32">
                    <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
                        <motion.div
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                            className="lg:col-span-7"
                        >
                            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold tracking-wide uppercase mb-6" style={{ background: '#FFF200', color: '#231F20' }} data-testid="hero-badge">
                                <Sparkles className="h-3.5 w-3.5" /> Powered by LearningHub.ai
                            </div>
                            <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl xl:text-7xl tracking-tight text-slate-900 text-balance">
                                Build India's most{' '}
                                <span className="relative inline-block">
                                    <span className="absolute inset-x-0 bottom-1 h-3 sm:h-4" style={{ background: '#FFF200', zIndex: 0 }} />
                                    <span className="relative z-10 text-slate-900">skilled</span>
                                </span>{' '}
                                cables &amp; wires workforce.
                            </h1>
                            <p className="mt-6 text-base sm:text-lg text-slate-600 max-w-2xl leading-relaxed">
                                One AI-native academy for every employee, channel partner, electrician, technician and distributor.
                                Courses, video reels, daily stories, an 8-game arcade, certificates and rewards — in 10 Indian languages, right on your phone.
                            </p>
                            <div className="mt-8 flex flex-wrap gap-3">
                                <Link
                                    to="/login"
                                    className="inline-flex items-center gap-2 px-6 py-3.5 rounded-2xl bg-brand-blue text-white font-semibold hover:bg-brand-blueDark transition shadow-lg shadow-brand-blue/20"
                                    data-testid="hero-cta-start"
                                >
                                    Start Learning <ArrowRight className="h-4 w-4" />
                                </Link>
                                <a
                                    href="#arcade"
                                    className="inline-flex items-center gap-2 px-6 py-3.5 rounded-2xl bg-white border border-slate-200 text-slate-900 font-semibold hover:border-slate-300"
                                    data-testid="hero-cta-explore"
                                >
                                    <Gamepad2 className="h-4 w-4" /> Peek inside the Arcade
                                </a>
                            </div>
                            <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-4 text-sm">
                                <Stat value={stats?.courses ?? '3+'} label="Live Courses" />
                                <Stat value="11" label="User Roles" />
                                <Stat value={games.length || '8'} label="Arcade Games" />
                                <Stat value="10" label="Languages" />
                            </div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.6, delay: 0.15 }}
                            className="lg:col-span-5 relative"
                        >
                            <ProgressVisual />
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* WHAT'S INSIDE — feature glance strip */}
            <section id="features" className="py-20 lg:py-28 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="max-w-2xl">
                        <div className="text-xs font-bold tracking-[0.2em] uppercase text-brand-blue mb-3">Everything Inside</div>
                        <h2 className="font-display text-3xl lg:text-5xl font-bold tracking-tight text-slate-900">
                            A full learning universe — not just a course list.
                        </h2>
                        <p className="mt-4 text-slate-600 text-lg">
                            Here's a glimpse of everything you get the moment you log in.
                        </p>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-12">
                        {FEATURES.map((f, i) => (
                            <motion.div
                                key={f.title}
                                initial={{ opacity: 0, y: 18 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.4, delay: (i % 4) * 0.05 }}
                                data-testid={`feature-${i}`}
                                className="group relative bg-white rounded-3xl p-6 border border-slate-200 hover:border-slate-300 hover:shadow-xl transition-all"
                            >
                                <div className={`h-12 w-12 rounded-2xl bg-gradient-to-br ${f.color} flex items-center justify-center ${f.dark ? 'text-brand-black' : 'text-white'} mb-4 group-hover:scale-110 transition-transform`}>
                                    <f.icon className="h-5 w-5" />
                                </div>
                                <h3 className="font-display font-bold text-lg text-slate-900">{f.title}</h3>
                                <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{f.desc}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ARCADE PREVIEW — REAL game visuals */}
            <section id="arcade" className="py-20 lg:py-28 bg-slate-950 text-white relative overflow-hidden">
                <div className="absolute inset-0 opacity-40 pointer-events-none">
                    <div className="absolute top-10 left-10 w-72 h-72 rounded-full bg-brand-yellow/20 blur-3xl" />
                    <div className="absolute bottom-0 right-10 w-80 h-80 rounded-full bg-brand-red/20 blur-3xl" />
                    <div className="absolute top-1/2 left-1/3 w-64 h-64 rounded-full bg-violet-600/20 blur-3xl" />
                </div>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
                    <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
                        <div className="max-w-2xl">
                            <div className="text-xs font-bold tracking-[0.2em] uppercase text-amber-300 mb-3 inline-flex items-center gap-1.5">
                                <Gamepad2 className="h-3.5 w-3.5" /> The Learning Arcade
                            </div>
                            <h2 className="font-display text-3xl lg:text-5xl font-bold tracking-tight">
                                Learn by playing.<br />Win real rewards.
                            </h2>
                            <p className="mt-5 text-slate-300 max-w-lg leading-relaxed">
                                {games.length || 8} fully-playable games built on cables &amp; wires knowledge — from a
                                <b className="text-amber-300"> KBC hot-seat</b> with TTS to multiplayer-style Ludo, Spin Wheel and Quiz Battle.
                                Every play earns points and badges.
                            </p>
                        </div>
                        <Link
                            to="/login"
                            className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl bg-brand-yellow text-brand-black font-bold hover:brightness-105 shadow-lg shrink-0"
                            style={{ boxShadow: '0 0 30px rgba(255,242,0,0.35)' }}
                            data-testid="arcade-cta"
                        >
                            Enter the Arcade <ArrowRight className="h-4 w-4" />
                        </Link>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-5">
                        {(sortedGames.length ? sortedGames : ORDER.map((code) => ({ code, name: code, description: '', points_per_play: 50 })))
                            .slice(0, 8)
                            .map((g, i) => (
                                <GamePreviewCard key={g.code} game={g} index={i} />
                            ))}
                    </div>
                </div>
            </section>

            {/* REELS + STORIES showcase */}
            <section className="py-20 lg:py-28 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
                    <PhoneMockup />
                    <div>
                        <div className="text-xs font-bold tracking-[0.2em] uppercase text-rose-500 mb-3 inline-flex items-center gap-1.5">
                            <Film className="h-3.5 w-3.5" /> Reels &amp; Stories
                        </div>
                        <h2 className="font-display text-3xl lg:text-5xl font-bold tracking-tight text-slate-900">
                            Training that feels like your favourite app.
                        </h2>
                        <p className="mt-5 text-slate-600 text-lg leading-relaxed">
                            Swipe through a vertical reels feed of 30-second product demos and safety drills.
                            Catch daily stories on new launches, schemes and tips. Like, rate and pick up where you left off — all designed for the phone in your pocket.
                        </p>
                        <ul className="mt-7 space-y-3">
                            {[
                                { icon: PlayCircle, t: 'Vertical reels feed with progress tracking' },
                                { icon: CircleDot, t: 'Daily 24-hour stories with launch CTAs' },
                                { icon: BellRing, t: 'In-app notifications keep you on track' },
                                { icon: Repeat, t: 'Replay games & quizzes to earn even more' },
                            ].map((x) => (
                                <li key={x.t} className="flex items-center gap-3 text-slate-700">
                                    <span className="h-8 w-8 rounded-xl bg-rose-50 flex items-center justify-center text-rose-500 shrink-0">
                                        <x.icon className="h-4 w-4" />
                                    </span>
                                    <span className="text-sm font-medium">{x.t}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            {/* GAMIFICATION / LEADERBOARD band */}
            <section className="py-20 lg:py-28 bg-gradient-to-br from-brand-blue to-brand-blueDark text-white relative overflow-hidden">
                <div className="absolute inset-0 grain-overlay opacity-30" />
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
                    <div className="max-w-2xl">
                        <div className="text-xs font-bold tracking-[0.2em] uppercase text-brand-yellow mb-3 inline-flex items-center gap-1.5">
                            <Crown className="h-3.5 w-3.5" /> Points · Badges · Leaderboards
                        </div>
                        <h2 className="font-display text-3xl lg:text-5xl font-bold tracking-tight">
                            Every action earns. Climb the ranks.
                        </h2>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
                        {[
                            { icon: Trophy, k: 'Live Leaderboard', v: 'Compete with your region & role' },
                            { icon: Medal, k: 'Badges & Streaks', v: 'Unlock achievements as you grow' },
                            { icon: Award, k: 'Verifiable Certificates', v: 'QR-checkable, 1-year validity' },
                            { icon: Gift, k: 'Redeemable Rewards', v: 'Spend points across ABG loyalty' },
                        ].map((x, i) => (
                            <motion.div
                                key={x.k}
                                initial={{ opacity: 0, y: 18 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: i * 0.08 }}
                                className="bg-white/10 backdrop-blur rounded-3xl p-6 border border-white/15"
                                data-testid={`gamify-${i}`}
                            >
                                <x.icon className="h-7 w-7 text-brand-yellow mb-4" />
                                <div className="font-display font-bold text-lg">{x.k}</div>
                                <div className="text-sm text-white/75 mt-1">{x.v}</div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CATEGORIES */}
            <section id="categories" className="py-20 lg:py-28 bg-slate-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="max-w-2xl">
                        <div className="text-xs font-bold tracking-[0.2em] uppercase text-brand-red mb-3">Training Categories</div>
                        <h2 className="font-display text-3xl lg:text-5xl font-bold tracking-tight text-slate-900">
                            Practical, India-context training for every role.
                        </h2>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
                        {CATEGORIES.map((c, i) => (
                            <motion.div
                                key={c.title}
                                initial={{ opacity: 0, y: 18 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.45, delay: i * 0.05 }}
                                data-testid={`category-${i}`}
                                className="group relative bg-white rounded-3xl p-7 border border-slate-200 hover:border-slate-300 hover:shadow-xl transition-all overflow-hidden"
                            >
                                <div className={`h-12 w-12 rounded-2xl bg-gradient-to-br ${c.color} flex items-center justify-center text-white mb-5 group-hover:scale-110 transition-transform`}>
                                    <c.icon className="h-5 w-5" />
                                </div>
                                <h3 className="font-display font-bold text-xl text-slate-900">{c.title}</h3>
                                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{c.desc}</p>
                                <div className="mt-5 inline-flex items-center gap-1 text-xs font-bold text-brand-blue">
                                    Explore <ChevronRight className="h-3.5 w-3.5" />
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CERTIFICATIONS */}
            <section id="certifications" className="py-20 lg:py-28 bg-slate-900 text-white relative overflow-hidden">
                <div className="absolute inset-0 grain-overlay" />
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
                    <div className="text-xs font-bold tracking-[0.2em] uppercase text-brand-orange mb-3">Certifications</div>
                    <h2 className="font-display text-3xl lg:text-5xl font-bold tracking-tight">
                        Verifiable certificates.<br />Recognised across the Propulse network.
                    </h2>
                    <div className="grid lg:grid-cols-3 gap-6 mt-12">
                        {['Certified Cables & Wires Product Expert', 'Certified Electrical Safety Champion', 'Certified Propulse Channel Partner'].map((title, i) => (
                            <motion.div
                                key={title}
                                initial={{ opacity: 0, y: 18 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: i * 0.1 }}
                                data-testid={`cert-card-${i}`}
                                className="bg-gradient-to-br from-slate-800 to-slate-900 border border-slate-700 rounded-3xl p-7 relative overflow-hidden"
                            >
                                <Award className="h-8 w-8 text-brand-orange mb-4" />
                                <div className="text-[10px] tracking-[0.2em] uppercase text-slate-400 mb-1">Propulse Cables & Wires Academy</div>
                                <div className="font-display font-bold text-xl">{title}</div>
                                <div className="text-xs text-slate-400 mt-3">QR-verifiable • 1-year validity</div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section id="about" className="py-20 lg:py-28 bg-white">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
                    <h2 className="font-display text-3xl lg:text-5xl font-bold text-slate-900 tracking-tight">
                        Your career has a learning runway. Start today.
                    </h2>
                    <p className="mt-5 text-slate-600 text-lg">
                        Join thousands of employees, dealers and technicians already learning on the Propulse Academy.
                    </p>
                    <Link
                        to="/login"
                        className="inline-flex items-center gap-2 px-6 py-3.5 rounded-2xl bg-brand-blue text-white font-semibold mt-8 hover:bg-brand-blueDark shadow-lg shadow-brand-blue/20"
                        data-testid="cta-final"
                    >
                        Start Learning <ArrowRight className="h-4 w-4" />
                    </Link>
                </div>
            </section>
        </div>
    );
}

function BannerCarousel() {
    const [idx, setIdx] = useState(0);
    const [dir, setDir] = useState(1);
    const count = BANNERS.length;

    const go = (n) => { setDir(n > idx || (idx === count - 1 && n === 0) ? 1 : -1); setIdx(((n % count) + count) % count); };
    const next = () => { setDir(1); setIdx((p) => (p + 1) % count); };
    const prev = () => { setDir(-1); setIdx((p) => (p - 1 + count) % count); };

    useEffect(() => {
        const t = setInterval(() => setIdx((p) => (p + 1) % count), 5500);
        return () => clearInterval(t);
    }, [count]);

    const b = BANNERS[idx];
    const Art = b.Art;

    return (
        <section className="bg-white pt-5 sm:pt-6" data-testid="banner-carousel">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className={`relative overflow-hidden rounded-3xl ring-1 ring-white/10 shadow-2xl bg-gradient-to-br ${b.bg} transition-[background] duration-700`}>
                    {/* glow accents */}
                    <div className="absolute inset-0 opacity-50 pointer-events-none">
                        <div className="absolute -top-10 -left-10 w-72 h-72 rounded-full bg-white/10 blur-3xl" />
                        <div className="absolute bottom-0 right-0 w-80 h-80 rounded-full bg-white/10 blur-3xl" />
                    </div>

                    <div className="relative grid lg:grid-cols-2 items-center min-h-[300px] sm:min-h-[360px]">
                        <AnimatePresence mode="wait" custom={dir}>
                            <motion.div
                                key={idx}
                                custom={dir}
                                initial={{ opacity: 0, x: dir * 40 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: dir * -40 }}
                                transition={{ duration: 0.45, ease: 'easeOut' }}
                                className="p-7 sm:p-12 text-white"
                            >
                                <div className={`inline-flex items-center gap-1.5 text-[11px] font-bold tracking-[0.2em] uppercase ${b.accent} mb-3`}>
                                    <Sparkles className="h-3.5 w-3.5" /> {b.eyebrow}
                                </div>
                                <h2 className="font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05]">
                                    {b.title}
                                </h2>
                                <p className="mt-4 text-sm sm:text-base text-white/80 max-w-md leading-relaxed">{b.sub}</p>
                                <Link
                                    to="/login"
                                    className="inline-flex items-center gap-2 px-5 py-3 rounded-2xl bg-white text-slate-900 font-bold mt-6 hover:bg-slate-100 transition shadow-lg"
                                    data-testid={`banner-cta-${idx}`}
                                >
                                    {b.cta} <ArrowRight className="h-4 w-4" />
                                </Link>
                            </motion.div>
                        </AnimatePresence>

                        {/* Art panel */}
                        <div className="relative h-44 sm:h-full min-h-[180px] hidden sm:block">
                            <AnimatePresence mode="wait">
                                <motion.div
                                    key={`art-${idx}`}
                                    initial={{ opacity: 0, scale: 0.92 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.92 }}
                                    transition={{ duration: 0.5 }}
                                    className="absolute inset-4 sm:inset-8 rounded-2xl overflow-hidden ring-1 ring-white/15"
                                >
                                    <Art />
                                </motion.div>
                            </AnimatePresence>
                        </div>
                    </div>

                    {/* Arrows */}
                    <button onClick={prev} aria-label="Previous banner" data-testid="banner-prev"
                        className="absolute left-3 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-black/30 backdrop-blur hover:bg-black/50 text-white flex items-center justify-center transition">
                        <ChevronLeft className="h-5 w-5" />
                    </button>
                    <button onClick={next} aria-label="Next banner" data-testid="banner-next"
                        className="absolute right-3 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-black/30 backdrop-blur hover:bg-black/50 text-white flex items-center justify-center transition">
                        <ChevronRight className="h-5 w-5" />
                    </button>

                    {/* Dots */}
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2">
                        {BANNERS.map((_, i) => (
                            <button
                                key={i}
                                onClick={() => go(i)}
                                aria-label={`Go to banner ${i + 1}`}
                                data-testid={`banner-dot-${i}`}
                                className={`h-2 rounded-full transition-all ${i === idx ? 'w-7 bg-white' : 'w-2 bg-white/40 hover:bg-white/70'}`}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}

function CertArt() {
    return (
        <div className="absolute inset-0 flex items-center justify-center">
            <div className="grid grid-cols-2 gap-3 -rotate-6">
                {[0, 1, 2, 3].map((i) => (
                    <motion.div
                        key={i}
                        animate={{ y: [0, -6, 0] }}
                        transition={{ repeat: Infinity, duration: 3, delay: i * 0.3 }}
                        className="w-24 h-16 sm:w-28 sm:h-20 rounded-xl bg-gradient-to-br from-slate-800 to-slate-900 ring-1 ring-brand-yellow/40 flex flex-col items-center justify-center shadow-xl"
                    >
                        <Award className="h-5 w-5 text-brand-yellow" />
                        <div className="mt-1 h-1 w-12 rounded bg-white/20" />
                        <div className="mt-1 h-1 w-8 rounded bg-white/10" />
                    </motion.div>
                ))}
            </div>
        </div>
    );
}

function LanguageArt() {
    const langs = ['नमस्ते', 'வணக்கம்', 'ನಮಸ್ಕಾರ', 'নমস্কার', 'નમસ્તે', 'Hello'];
    return (
        <div className="absolute inset-0 flex items-center justify-center p-6">
            <div className="flex flex-wrap gap-2.5 justify-center max-w-xs">
                {langs.map((l, i) => (
                    <motion.span
                        key={l}
                        animate={{ scale: [1, 1.08, 1], opacity: [0.8, 1, 0.8] }}
                        transition={{ repeat: Infinity, duration: 2.4, delay: i * 0.25 }}
                        className="px-3.5 py-2 rounded-full bg-white/15 backdrop-blur text-white font-display font-bold text-sm ring-1 ring-white/25"
                    >
                        {l}
                    </motion.span>
                ))}
            </div>
        </div>
    );
}

function GamePreviewCard({ game, index }) {
    const conf = ILLUSTRATIONS[game.code] || { Render: () => null, bg: 'from-slate-700 to-slate-900', tag: 'GAME', accent: 'text-slate-200' };
    const Illustration = conf.Render;
    return (
        <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            whileInView={{ opacity: 1, y: 0, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: (index % 8) * 0.05, type: 'spring', stiffness: 180, damping: 18 }}
            whileHover={{ y: -6 }}
            className="group"
            data-testid={`home-game-${game.code}`}
        >
            <Link to="/login" className={`relative block rounded-3xl overflow-hidden ring-1 ring-white/10 shadow-xl bg-gradient-to-br ${conf.bg} text-white`}>
                <div className="relative h-32 sm:h-40 overflow-hidden">
                    <Illustration />
                    <div className="absolute inset-0 pointer-events-none opacity-[0.06]"
                        style={{ backgroundImage: 'repeating-linear-gradient(45deg, rgba(255,255,255,0.5) 0 1px, transparent 1px 6px)' }} />
                    <div className="absolute top-2.5 left-2.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-black/40 backdrop-blur text-[8px] font-bold uppercase tracking-wider">
                        <span className={conf.accent}>● {conf.tag}</span>
                    </div>
                </div>
                <div className="relative p-3 sm:p-4">
                    <div className="font-display font-extrabold text-sm sm:text-base leading-tight truncate">{game.name}</div>
                    <div className="mt-2 flex items-center justify-between">
                        <span className="inline-flex items-center gap-1 text-[9px] sm:text-[10px] font-bold bg-white/20 backdrop-blur px-2 py-0.5 rounded-full">
                            +{game.points_per_play} pts
                        </span>
                        <span className="inline-flex items-center gap-1 text-[10px] sm:text-xs font-bold text-white group-hover:translate-x-1 transition-transform">
                            Play <ArrowRight className="h-3 w-3" />
                        </span>
                    </div>
                </div>
                <div className="absolute -inset-px pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-3xl"
                    style={{ boxShadow: '0 0 50px rgba(255,255,255,0.15), 0 20px 50px rgba(0,0,0,0.4)' }} />
            </Link>
        </motion.div>
    );
}

function PhoneMockup() {
    return (
        <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative mx-auto"
            data-testid="reels-phone-mockup"
        >
            <div className="absolute -inset-8 bg-gradient-to-br from-rose-200/40 to-fuchsia-200/30 rounded-[3rem] blur-3xl" />
            <div className="relative mx-auto w-[260px] h-[520px] rounded-[2.5rem] bg-slate-900 p-2.5 shadow-2xl ring-1 ring-slate-800">
                <div className="absolute top-3 left-1/2 -translate-x-1/2 w-24 h-5 bg-slate-900 rounded-full z-20" />
                <div className="relative w-full h-full rounded-[2rem] overflow-hidden bg-gradient-to-b from-slate-800 to-black">
                    {/* Story ring strip */}
                    <div className="absolute top-0 inset-x-0 z-10 flex gap-2.5 p-3 pt-6 bg-gradient-to-b from-black/70 to-transparent">
                        {['from-yellow-400 to-rose-500', 'from-emerald-400 to-teal-500', 'from-sky-400 to-violet-500', 'from-fuchsia-400 to-pink-500'].map((g, i) => (
                            <div key={i} className={`h-9 w-9 rounded-full bg-gradient-to-br ${g} p-[2px] shrink-0`}>
                                <div className="h-full w-full rounded-full bg-slate-800 ring-2 ring-slate-900" />
                            </div>
                        ))}
                    </div>
                    {/* Reel content */}
                    <div className="absolute inset-0 flex items-center justify-center">
                        <motion.div
                            animate={{ scale: [1, 1.06, 1], opacity: [0.85, 1, 0.85] }}
                            transition={{ repeat: Infinity, duration: 3 }}
                            className="h-16 w-16 rounded-full bg-white/15 backdrop-blur flex items-center justify-center ring-1 ring-white/30"
                        >
                            <PlayCircle className="h-8 w-8 text-white" />
                        </motion.div>
                    </div>
                    <div className="absolute inset-x-0 bottom-0 p-4 pb-6 bg-gradient-to-t from-black/80 to-transparent">
                        <div className="text-white text-xs font-bold">FRLS vs FR — 30-sec demo</div>
                        <div className="text-white/60 text-[10px] mt-0.5">#cablesafety · Propulse Reels</div>
                        <div className="mt-2 flex gap-3 text-white/90 text-[10px]">
                            <span className="inline-flex items-center gap-1"><Star className="h-3 w-3 text-amber-300" /> 4.8</span>
                            <span className="inline-flex items-center gap-1"><Zap className="h-3 w-3 text-brand-yellow" /> +20 pts</span>
                        </div>
                    </div>
                    {/* Right action rail */}
                    <div className="absolute right-2.5 bottom-24 flex flex-col gap-4 text-white/90">
                        {[Trophy, BellRing, Users].map((Icon, i) => (
                            <div key={i} className="h-9 w-9 rounded-full bg-white/10 backdrop-blur flex items-center justify-center">
                                <Icon className="h-4 w-4" />
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </motion.div>
    );
}

function Stat({ value, label }) {
    return (
        <div className="flex items-center gap-2">
            <div className="font-display font-extrabold text-2xl text-slate-900">{value}</div>
            <div className="text-xs text-slate-500 uppercase tracking-wide">{label}</div>
        </div>
    );
}

function ProgressVisual() {
    const { theme } = useTheme();
    const dark = theme === 'dark';
    return (
        <div className="relative aspect-square w-full max-w-md mx-auto" data-testid="hero-progress-visual">
            <div className="absolute inset-0 bg-gradient-to-br from-brand-yellow/15 to-brand-red/5 rounded-full blur-3xl" />
            <svg viewBox="0 0 200 200" className="relative w-full">
                <defs>
                    <linearGradient id="ringGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#FFF200" />
                        <stop offset="100%" stopColor="#E31E24" />
                    </linearGradient>
                </defs>
                <circle cx="100" cy="100" r="84" fill="none" stroke={dark ? '#283142' : '#E2E8F0'} strokeWidth="14" />
                <motion.circle
                    cx="100" cy="100" r="84" fill="none" stroke="url(#ringGrad)" strokeWidth="14" strokeLinecap="round"
                    strokeDasharray="528" transform="rotate(-90 100 100)"
                    initial={{ strokeDashoffset: 528 }}
                    animate={{ strokeDashoffset: 528 * 0.22 }}
                    transition={{ duration: 1.6, ease: 'easeOut' }}
                />
                <text x="100" y="92" textAnchor="middle" className="font-display font-extrabold" fontSize="44" fill={dark ? '#F1F5F9' : '#0F172A'}>
                    78%
                </text>
                <text x="100" y="118" textAnchor="middle" fontSize="11" fill={dark ? '#8A93A6' : '#64748B'} fontWeight="600">
                    course mastery
                </text>
            </svg>
            <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="absolute -left-2 top-6 bg-white rounded-2xl shadow-lg border border-slate-200 px-3 py-2 flex items-center gap-2"
            >
                <div className="h-8 w-8 rounded-xl bg-amber-100 flex items-center justify-center"><Zap className="h-4 w-4 text-amber-600" /></div>
                <div>
                    <div className="text-xs font-bold text-slate-900">Fast Learner</div>
                    <div className="text-[10px] text-slate-500">+200 pts</div>
                </div>
            </motion.div>
            <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
                className="absolute -right-2 bottom-10 bg-white rounded-2xl shadow-lg border border-slate-200 px-3 py-2 flex items-center gap-2"
            >
                <div className="h-8 w-8 rounded-xl bg-emerald-100 flex items-center justify-center"><ShieldCheck className="h-4 w-4 text-emerald-600" /></div>
                <div>
                    <div className="text-xs font-bold text-slate-900">Safety Champion</div>
                    <div className="text-[10px] text-slate-500">Certified</div>
                </div>
            </motion.div>
        </div>
    );
}
